import React from 'react';
import Typography from '@mui/joy/Typography';
import Divider from "@mui/joy/Divider";
import Sheet from '@mui/joy/Sheet';
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Button from "@mui/joy/Button";
import Input from "@mui/joy/Input";
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import {NotificationService} from "../../common/notification";
import CircularProgress from "@mui/joy/CircularProgress";
import { orderForceActionApi } from "./orderApi";
import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";
import Backdrop from '@mui/material/Backdrop';
import { ForceActionDescription, ForceActionName, ForceActionType, ForceOrderResponse, OrderType } from '../../models/order';
import Radio from '@mui/joy/Radio';
import RadioGroup from '@mui/joy/RadioGroup';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/joy/Box';
import { formatRubles } from "../../common/money";

type DefaultProps = {
    // Для ForceActionType.Handle orderType не обязателен и заказы могут быть разных типов.
    // Для остальных действий все заказы в orderIds должны быть одного типа orderType.
    orderIds: number[],
    orderType?: OrderType,
    totalAmount: number,
    actionType: ForceActionType,
    modalOpen: boolean,
    setModalOpen: (e: boolean) => void,
    onCompleted: (success: boolean, actionType: ForceActionType, successIds: number[], failedIds: number[]) => void
}

// Действия с возвратом денег покупателю: для P2P возврат оформляется через отмену
const getRefundActionType = (orderType?: OrderType): ForceActionType =>
    orderType === OrderType.P2P ? ForceActionType.Cancel : ForceActionType.Refund;

const normalizeActionType = (actionType: ForceActionType, orderType?: OrderType): ForceActionType =>
    actionType === ForceActionType.Refund ? getRefundActionType(orderType) : actionType;

export function OrderActions(props: DefaultProps) {
    const {orderIds, orderType, totalAmount, actionType, modalOpen, setModalOpen, onCompleted} = props;
    const refundActionType = getRefundActionType(orderType);

    const [backdrop, setBackdrop] = React.useState(false);

    const [selectedActionType, setSelectedActionType] = React.useState<ForceActionType>(ForceActionType.Complete);
    const [reason, setReason] = React.useState<string>('');

    const [results, setResults] = React.useState<ForceOrderResponse[]>([]);
    const [resultOpen, setResultOpen] = React.useState(false);
    const [resultActionType, setResultActionType] = React.useState<ForceActionType>(ForceActionType.Handle);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    React.useEffect(() => {
        if (!modalOpen) {
            return;
        }

        if (actionType !== ForceActionType.Handle && orderType === undefined) {
            NotificationService.alertError("Не удалось определить тип заказов. Завершение возможно только для заказов одного типа.");
            setModalOpen(false);
            return;
        }

        setResultOpen(false);
        setResults([]);
        setSelectedActionType(normalizeActionType(actionType, orderType));
    }, [modalOpen, actionType, orderType, setModalOpen])

    const formatResult = React.useCallback(
        (success: boolean, error?: string): string => {
            switch (resultActionType) {
                case ForceActionType.Handle:
                    return success
                        ? 'Обработка запущена'
                        : 'Не удалось запустить обработку' + (error ? `: ${error}` : '');
                case ForceActionType.Complete:
                    return success
                        ? 'Успешно'
                        : 'Не удалось завершить заказ' + (error ? `: ${error}` : '');
                case ForceActionType.Cancel:
                    return success
                        ? 'Успешная отмена'
                        : 'Отмена не удалась' + (error ? `: ${error}` : '');
                case ForceActionType.Refund:
                    return success
                        ? 'Возврат оформлен'
                        : 'Не удалось оформить возврат' + (error ? `: ${error}` : '');
            }
        },
        [resultActionType],
    );

    const applyForceResult = (success: boolean, executedAction: ForceActionType, actionResults: ForceOrderResponse[]) => {
        if (!success) {
            NotificationService.alertError("Ошибка запроса. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            onCompleted(false, executedAction, [], orderIds);
            return;
        }

        setResults(actionResults);
        setResultActionType(executedAction);
        setResultOpen(true);
        onCompleted(
            true,
            executedAction,
            actionResults.filter((e) => e.success).map((e) => e.orderId),
            actionResults.filter((e) => !e.success).map((e) => e.orderId),
        );
    }

    const setOrderForceCompleted = async () => {
        if ((orderIds ?? []).length <= 0) {
            return;
        }

        setBackdrop(true)

        try {
            const result = await orderForceActionApi(orderIds!, selectedActionType, reason);
            applyForceResult(result.result, selectedActionType, result.value ?? []);
        } finally {
            setBackdrop(false);
        }
    }

    const onForceHandle = async () => {
        if ((orderIds ?? []).length <= 0) {
            return;
        }

        setBackdrop(true)

        try {
            const result = await orderForceActionApi(orderIds!, ForceActionType.Handle);
            applyForceResult(result.result, ForceActionType.Handle, result.value ?? []);
        } finally {
            setBackdrop(false);
        }
    }

    return (
        <React.Fragment>
            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={backdrop}
                onClick={() => setBackdrop(false)}
            >
                <CircularProgress />
            </Backdrop>

            {(orderIds ?? []).length > 0
            ? <React.Fragment>
            <Modal open={modalOpen && actionType !== ForceActionType.Handle && orderType !== undefined} onClose={() => setModalOpen(false)}>
                <ModalDialog layout={fullScreen ? 'fullscreen' : 'center'} sx={fullScreen ? {marginTop: '50px'} : {}}>
                    <ModalClose/>
                    <Typography id="filter-modal" level="h2">{`Завершить заказы (#): ${orderIds.join(', ')}`}</Typography>
                    <Divider sx={{my: 2}}/>
                    <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        <Alert variant="outlined" severity="info">{ForceActionDescription[selectedActionType]}</Alert>
                        <FormControl>
                            <FormLabel>Комментарий</FormLabel>
                            <Input placeholder="Комментарий" defaultValue={reason} value={reason}
                                   onChange={(event) => setReason(event.target.value)}/>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Способ завершения</FormLabel>
                            <RadioGroup defaultValue="medium"
                                        name="radio-buttons-group"
                                        value={Number(selectedActionType)}>
                                <List 
                                    sx={{
                                    minWidth: 240,
                                    '--List-gap': '0.5rem',
                                    '--ListItem-paddingY': '1rem',
                                    '--ListItem-radius': '8px',
                                    '--ListItemDecorator-size': '32px',
                                    }}
                                >
                                    {[refundActionType, ForceActionType.Complete].map((item, index) => (
                                        <ListItem variant="outlined" key={item} sx={{ boxShadow: 'sm' }}>
                                            <ListItemDecorator>
                                                {[<CurrencyExchangeIcon />, <TaskAltIcon />][index]}
                                            </ListItemDecorator>
                                            <Radio
                                                overlay
                                                value={Number(item)}
                                                label={item === ForceActionType.Refund
                                                    ? ForceActionName[item] + ' ' + formatRubles(totalAmount)
                                                    : ForceActionName[item]}
                                                sx={{ flexGrow: 1, flexDirection: 'row-reverse' }}
                                                onChange={async (event) => {
                                                    if (event.target.checked) {
                                                        setSelectedActionType(item);
                                                    }
                                                }}
                                                slotProps={{
                                                    action: ({ checked }) => ({
                                                        sx: (theme) => ({
                                                            ...(checked && {
                                                            inset: -1,
                                                            border: '2px solid',
                                                            borderColor: theme.vars.palette.primary[500],
                                                            }),
                                                        }),
                                                    }),
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </RadioGroup>
                        </FormControl>
                        <Button
                            onClick={() => {
                                setModalOpen(false);
                                setOrderForceCompleted();
                            }}
                            color="primary"
                        >
                            Запуск
                        </Button>
                    </Sheet>
                </ModalDialog>
            </Modal>

            <ConfirmDialog
                title="Принудительный запуск."
                open={modalOpen && actionType === ForceActionType.Handle}
                setOpen={setModalOpen}
                onConfirm={onForceHandle}
            >
                {`Будет произведена принудительная внеочередная обработка заказов (#): ${orderIds?.join(', ') ?? ''}. Продолжить?`}
            </ConfirmDialog>
            </React.Fragment>
            : <></>}

            <Dialog
                open={resultOpen}
                fullScreen={fullScreen}
                onClose={() => setResultOpen(false)}
            >
                <DialogTitle>Результаты</DialogTitle>
                <DialogContent>
                    <Box
                        noValidate
                        component="form"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            overflowY: 'scroll',
                            overflowX: 'hidden'
                        }}
                    >
                        {results.map(row => (
                            <Typography level="body-xs" >Заказ #{row.orderId} - {formatResult(row.success, row.error)}</Typography>
                        ))}                        
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="neutral" onClick={() => setResultOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
